import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ArrowPathIcon, ChevronRightIcon, EnvelopeIcon } from '@heroicons/react/24/outline'
import * as api from '../../api/account'
import { Button, ErrorText, SectionTitle, inputClass } from './ui'
import Awatar from '../zespol/Awatar'
import Przelacznik from '../ui/Przelacznik'
import { usePlan, useMozna } from '../../hooks/usePlan'
import { KlodkaPremium } from '../plany/BramkaPremium'
import { useOknoPremium } from '../plany/OknoPremiumContext'
import { CENNIK, KONTAKT_PREMIUM, LIMIT_KONT } from '../../config/plany'
import { formatKwota } from '../../utils'

/**
 * Zespół firmy (konta zespołowe, decyzja właściciela 2026-10-11).
 *
 * Kafelek na osobę (zdjęcie, imię, rola, liczba klientów); klik otwiera kartę osoby (`/zespol/:id`):
 * zdjęcie, uprawnienia, statystyki i lista jej klientów. Tutaj zaproszenia i limit kont (domyślnie 5,
 * `Company.limit_kont`; liczy konto główne, osoby i ważne zaproszenia). Listę uprawnień z nazwami i opisami
 * daje serwer (`accounts/zespol.py` UPRAWNIENIA). Reguły pilnuje API; ten ekran tylko je pokazuje.
 * Pełny opis: second-brain/02_PLATFORMA_MODULY.md, „Zespół firmy”.
 *
 * Plany (FE-10, decyzja 2026-10-19): limit kont liczy serwer z planu (Free 2, Premium 5 + 10 za każdy pakiet).
 * Przy pełnym zespole podpowiadamy Premium albo pakiet kont; wybór uprawnień przy zaproszeniu jest w Premium
 * (`uprawnienia_zespolu`). Osoba ponad limitem po spadku planu ma `zablokowane_od` (kontrakt BE-04), a konto główne
 * wybiera, kto zostaje (`KtoZostaje`).
 */

const data = (iso) => new Date(iso).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long' })

const Kafelek = ({ o, katalog }) => {
  const wlasciciel = o.rola === 'owner'
  const nazwa = o.imie || o.email
  const nadane = katalog.filter((u) => o.uprawnienia.includes(u.klucz))
  return (
    <Link to={`/zespol/${o.id}`}
          className="group flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4 no-underline transition hover:border-blue-400 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
      <Awatar osoba={o} rozmiar={56} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-gray-900">
          {nazwa}{o.to_ja && <span className="ml-1.5 text-xs font-normal text-gray-400">to Ty</span>}
        </p>
        <p className="text-xs text-gray-500">
          {wlasciciel ? <span className="font-semibold text-blue-700">Konto główne</span> : 'Pracownik'}
          {', '}{o.klientow} {o.klientow === 1 ? 'klient' : 'klientów'}
        </p>
        <p className="mt-1 truncate text-xs text-gray-500">
          {wlasciciel ? 'Wszystkie uprawnienia' : nadane.length ? nadane.map((u) => u.nazwa).join(', ') : 'Tylko własni klienci'}
        </p>
        {o.zablokowane_od && (
          <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-600">
            <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-500" />
            Zablokowane od {data(o.zablokowane_od)}: ponad limit kont planu
          </p>
        )}
      </div>
      <ChevronRightIcon className="h-4 w-4 flex-shrink-0 text-gray-300 transition group-hover:text-blue-600" />
    </Link>
  )
}

const odmiana = (n, [jedna, kilka, wiele]) => {
  if (n === 1) return jedna
  const j = n % 10
  const d = n % 100
  return j >= 2 && j <= 4 && (d < 12 || d > 14) ? kilka : wiele
}

/**
 * Kto zostaje aktywny, gdy zespół jest większy niż limit planu, np. po końcu próby (FE-10, BE-04). Zablokowana
 * osoba loguje się, ale nie widzi klientów firmy. Konto główne jest zawsze aktywne, więc wybór dotyczy pracowników;
 * serwer dopełnia wolne miejsca najwcześniej dołączonymi (`PUT /zespol/aktywne`).
 */
const KtoZostaje = ({ zespol, busy, free, onZapisz, onPremium }) => {
  const pracownicy = zespol.osoby.filter((o) => o.rola !== 'owner')
  const miejsc = Math.max(zespol.limit - (zespol.osoby.length - pracownicy.length), 0)
  const [wybrani, setWybrani] = useState(() => pracownicy.filter((o) => !o.zablokowane_od).map((o) => o.id))
  const zaDuzo = wybrani.length > miejsc
  const n = zespol.osoby.length
  return (
    <div className="mt-6 border-t border-gray-100 pt-5">
      <h3 className="text-sm font-semibold text-gray-900">Kto zostaje aktywny</h3>
      <p className="mt-1 text-sm text-gray-600">
        Plan firmy mieści {zespol.limit} {odmiana(zespol.limit, ['konto', 'konta', 'kont'])} razem z kontem głównym,
        a w zespole {n === 1 ? 'jest' : 'są'} {n} {odmiana(n, ['osoba', 'osoby', 'osób'])}. Zablokowana osoba może się
        zalogować, ale nie widzi klientów firmy. Wybierz, kto pracuje dalej.
      </p>
      <div className="mt-3 grid gap-y-3">
        {pracownicy.map((o) => (
          <Przelacznik key={o.id} wartosc={wybrani.includes(o.id)} disabled={busy}
                       etykieta={<span className="font-medium text-gray-900">{o.imie || o.email}</span>}
                       opis={o.zablokowane_od ? `Zablokowane od ${data(o.zablokowane_od)}` : 'Aktywne'}
                       onZmiana={(wl) => setWybrani((l) => (wl ? [...l, o.id] : l.filter((x) => x !== o.id)))} />
        ))}
      </div>
      <p className={`mt-3 text-xs ${zaDuzo ? 'font-semibold text-red-600' : 'text-gray-500'}`}>
        Wybrano {wybrani.length} z {miejsc}{zaDuzo && '. Odznacz kogoś, żeby zapisać.'}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        <Button busy={busy} disabled={busy || zaDuzo} onClick={() => onZapisz(wybrani)}>Zapisz wybór</Button>
        {free && (
          <button type="button" onClick={onPremium} className="text-sm font-semibold text-blue-700 hover:underline">
            W Premium zespół ma {LIMIT_KONT.premium} kont
          </button>
        )}
      </div>
    </div>
  )
}

const ZespolSection = ({ profile, setFlash }) => {
  const bezFirmy = !profile?.company
  const [zespol, setZespol] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [email, setEmail] = useState('')
  const [noweUprawnienia, setNoweUprawnienia] = useState([])
  const location = useLocation()
  const navigate = useNavigate()
  const komunikatZKarty = location.state?.komunikat
  const plan = usePlan()
  const { mozna: planUprawnien } = useMozna('uprawnienia_zespolu')
  const { otworz: otworzPremium } = useOknoPremium()

  // Komunikat z karty osoby po usunięciu jej z zespołu (2026-10-11); czyścimy stan, żeby odświeżenie go nie powtórzyło.
  useEffect(() => {
    if (!komunikatZKarty) return
    setFlash?.(komunikatZKarty)
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
  }, [komunikatZKarty, setFlash, navigate, location.pathname, location.search])

  useEffect(() => {
    if (bezFirmy) return undefined
    let anulowane = false
    api.getZespol().then((res) => {
      if (anulowane) return
      if (res.ok) setZespol(res.data)
      else setError(res.error)
    })
    return () => { anulowane = true }
  }, [bezFirmy])

  const wykonaj = async (akcja, komunikat) => {
    setError('')
    setBusy(true)
    const res = await akcja()
    setBusy(false)
    if (!res.ok) { setError(res.error); return false }
    setZespol(res.data)
    const tekst = typeof komunikat === 'function' ? komunikat(res.data) : komunikat
    if (tekst) setFlash?.(tekst)
    return true
  }

  // Konto firmowe bez podpiętej firmy (NIP): zespół jest firmy, więc najpierw trzeba ją dodać.
  if (bezFirmy) {
    return (
      <div>
        <SectionTitle hint="Tu zapraszasz pracowników i ustawiasz, co mogą zmieniać.">Zespół</SectionTitle>
        <p className="text-sm text-gray-700">
          Zespół jest przypisany do firmy, a to konto nie ma jeszcze podpiętej firmy. Podaj NIP w zakładce „Firma”,
          a tu pojawią się pracownicy, zaproszenia i uprawnienia.
        </p>
        <Link to="/konto?sekcja=firma"
              className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white no-underline hover:bg-blue-700">
          Przejdź do zakładki Firma <ChevronRightIcon className="h-4 w-4" />
        </Link>
      </div>
    )
  }

  if (!zespol) return error ? <ErrorText>{error}</ErrorText> : <p className="py-6 text-sm text-gray-500">Wczytuję zespół…</p>

  const mozeZarzadzac = zespol.moje_uprawnienia.includes('zespol')
  const zaproszeniaWazne = zespol.zaproszenia.filter((z) => z.stan === 'wazne').length
  const pelny = zespol.zajete >= zespol.limit
  const jaWlasciciel = zespol.osoby.some((o) => o.to_ja && o.rola === 'owner')
  const katalog = zespol.uprawnienia

  const zapros = async (e) => {
    e.preventDefault()
    // Bez funkcji `uprawnienia_zespolu` nie wysyłamy wyboru (przełączniki są wtedy wyłączone).
    const uprawnienia = planUprawnien ? noweUprawnienia : []
    const ok = await wykonaj(() => api.zaprosDoZespolu(email.trim(), uprawnienia), (d) => (d.wyslano === false
      ? 'Zaproszenie zapisane, ale nie udało się wysłać maila. Wyślij je ponownie z listy.'
      : `Wysłaliśmy zaproszenie na ${email.trim()}.`))
    if (ok) { setEmail(''); setNoweUprawnienia([]) }
  }

  return (
    <div>
      <SectionTitle hint="Konto główne zaprasza osoby z firmy i decyduje, co każda z nich może zmieniać. Osoba bez uprawnień widzi tylko klientów, których sama dodała, i wycenia według stawek firmy.">
        Zespół {zespol.firma}
      </SectionTitle>

      <p className="text-sm text-gray-600">
        <span className="font-semibold tabular-nums text-gray-900">{zespol.zajete} z {zespol.limit}</span> kont w firmie
        {zaproszeniaWazne > 0 && <span className="text-gray-500">, w tym {zaproszeniaWazne} {zaproszeniaWazne === 1 ? 'zaproszenie' : 'zaproszenia'} w drodze</span>}
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {zespol.osoby.map((o) => <Kafelek key={o.id} o={o} katalog={katalog} />)}
      </div>

      {jaWlasciciel && zespol.osoby.some((o) => o.zablokowane_od) && (
        // Klucz z blokad: po zapisie wybór startuje od nowego stanu z serwera.
        <KtoZostaje key={zespol.osoby.map((o) => `${o.id}:${o.zablokowane_od ? 1 : 0}`).join(',')}
                    zespol={zespol} busy={busy} free={plan.stan === 'plan' && plan.plan === 'free'}
                    onPremium={() => otworzPremium()}
                    onZapisz={(osoby) => wykonaj(() => api.ustawAktywnych(osoby),
                      'Zapisane. Zablokowane osoby wrócą do pracy, gdy zwolni się miejsce albo firma przejdzie na Premium.')} />
      )}

      {mozeZarzadzac && zespol.zaproszenia.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-gray-900">Wysłane zaproszenia</h3>
          <ul className="mt-2 divide-y divide-gray-100">
            {zespol.zaproszenia.map((z) => (
              <li key={z.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3">
                <EnvelopeIcon className="h-4 w-4 flex-shrink-0 text-blue-600" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-gray-900">{z.email}</p>
                  <p className="flex items-center gap-1.5 text-xs text-gray-500">
                    <span className={`h-1.5 w-1.5 rounded-full ${z.stan === 'wazne' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                    {z.stan === 'wazne' ? `Ważne do ${data(z.wygasa)}` : 'Wygasło, nie zajmuje miejsca'}
                    {z.uprawnienia.length > 0 && `, ${z.uprawnienia.map((k) => katalog.find((u) => u.klucz === k)?.nazwa || k).join(', ')}`}
                  </p>
                </div>
                <button type="button" disabled={busy}
                        onClick={() => wykonaj(() => api.ponowZaproszenie(z.id), (d) => (d.wyslano === false
                          ? 'Nie udało się wysłać maila. Spróbuj za chwilę.' : `Wysłaliśmy nowy link na ${z.email}.`))}
                        className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50">
                  <ArrowPathIcon className="h-3.5 w-3.5" /> Wyślij ponownie
                </button>
                <button type="button" disabled={busy}
                        onClick={() => wykonaj(() => api.cofnijZaproszenie(z.id), 'Zaproszenie cofnięte, link już nie działa.')}
                        className="rounded-lg px-2 py-1 text-xs font-semibold text-gray-500 hover:bg-red-50 hover:text-red-600">
                  Cofnij
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {mozeZarzadzac && (
        <form onSubmit={zapros} className="mt-6 border-t border-gray-100 pt-5" data-samouczek="zapros">
          <h3 className="text-sm font-semibold text-gray-900">Zaproś osobę</h3>
          {pelny ? (
            <>
              <p className="mt-2 text-sm text-gray-600">
                Wykorzystano limit {zespol.limit} kont. Usuń osobę albo cofnij zaproszenie, żeby zaprosić kogoś nowego.
              </p>
              {plan.stan === 'plan' && plan.plan === 'free' && (
                <p className="mt-2 text-sm text-gray-600">
                  Plan Free ma {LIMIT_KONT.free} konta w firmie. W Premium jest {LIMIT_KONT.premium} kont, a każde
                  kolejne {CENNIK.pakietKont.kont} kont to {formatKwota(CENNIK.pakietKont.miesiecznie)} netto miesięcznie.{' '}
                  <button type="button" onClick={() => otworzPremium()} className="font-semibold text-blue-700 hover:underline">
                    Co daje Premium
                  </button>
                </p>
              )}
              {plan.stan === 'plan' && plan.plan === 'premium' && (
                <p className="mt-2 text-sm text-gray-600">
                  Kolejne {CENNIK.pakietKont.kont} kont to {formatKwota(CENNIK.pakietKont.miesiecznie)} netto miesięcznie.{' '}
                  <a href={`mailto:${KONTAKT_PREMIUM}?subject=${encodeURIComponent(`Pakiet kont dla firmy ${zespol.firma || ''}`.trim())}`}
                     className="font-semibold text-blue-700 hover:underline">
                    Napisz do nas o pakiet kont
                  </a>
                </p>
              )}
            </>
          ) : (
            <>
              <p className="mt-1 text-xs text-gray-500">Link w mailu jest ważny 7 dni i działa tylko dla konta z tym adresem.</p>
              <input type="email" required placeholder="adres@firma.pl" value={email} disabled={busy}
                     onChange={(e) => setEmail(e.target.value)} className={`${inputClass} mt-3 max-w-md`} />
              <fieldset className="mt-4">
                <legend className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  Nadaj uprawnienia {!planUprawnien && <KlodkaPremium />}
                </legend>
                <div className="mt-3 grid gap-x-6 gap-y-3 sm:grid-cols-2">
                  {katalog.map((u) => (
                    <Przelacznik key={u.klucz} etykieta={<span className="font-medium text-gray-900">{u.nazwa}</span>} opis={u.opis}
                                 wartosc={noweUprawnienia.includes(u.klucz)} disabled={busy || !planUprawnien}
                                 onZmiana={(wl) => setNoweUprawnienia((l) => (wl ? [...l, u.klucz] : l.filter((x) => x !== u.klucz)))} />
                  ))}
                </div>
                {planUprawnien ? (
                  <p className="mt-2 text-xs text-gray-500">Bez zaznaczeń: tylko własni klienci, wycena według stawek firmy.</p>
                ) : (
                  <p className="mt-2 text-xs text-gray-500">
                    Wybór uprawnień jest w planie Premium.{' '}
                    <button type="button" onClick={() => otworzPremium('uprawnienia_zespolu')}
                            className="font-semibold text-blue-700 hover:underline">Co daje Premium</button>
                  </p>
                )}
              </fieldset>
              <div className="mt-4"><Button type="submit" busy={busy} disabled={!email.trim()}>Wyślij zaproszenie</Button></div>
            </>
          )}
        </form>
      )}

      {!mozeZarzadzac && (
        <p className="mt-4 text-sm text-gray-500">Osoby do zespołu zaprasza konto główne albo osoba z uprawnieniem „Edycja zespołu”.</p>
      )}

      <ErrorText>{error}</ErrorText>
    </div>
  )
}

export default ZespolSection
